/**
 * 面接管理画面
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getInterviews, getInterview, deleteInterview } from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Search, Loader2, Eye, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function Interviews() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<any>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'interviews', search, page],
    queryFn: () => getInterviews({ search, page, limit: 20 }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteInterview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'interviews'] });
      setIsDeleteDialogOpen(false);
      setSelectedInterview(null);
      toast({
        title: '成功',
        description: '面接を削除しました',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'エラー',
        description: error.message || '面接の削除に失敗しました',
        variant: 'destructive',
      });
    },
  });

  const handleView = async (interview: any) => {
    try {
      const fullInterview = await getInterview(interview.id);
      setSelectedInterview(fullInterview);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast({
        title: 'エラー',
        description: '面接情報の取得に失敗しました',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = (interview: any) => {
    setSelectedInterview(interview);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedInterview) {
      deleteMutation.mutate(selectedInterview.id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">面接管理</h1>
          <p className="text-base text-gray-500 mt-2">面接履歴の管理</p>
        </div>

        {/* 検索 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="ユーザー名またはメールアドレスで検索..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-11 h-11 rounded-xl border-gray-200 bg-white"
            />
          </div>
        </div>

        {/* 面接一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">面接一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200 hover:bg-transparent">
                    <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">ユーザー</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">プロンプト名</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">ラウンド数</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">概要</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">作成日時</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.interviews?.map((interview: any) => (
                    <TableRow
                      key={interview.id}
                      className="border-b border-gray-100 cursor-pointer hover:bg-gray-50/50"
                      onClick={() => handleView(interview)}
                    >
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{interview.id}</TableCell>
                      <TableCell className="py-4">
                        <div>
                          <div className="text-base font-medium text-gray-900">{interview.user_name || '-'}</div>
                          <div className="text-base text-gray-500">{interview.user_email || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4 max-w-xs truncate text-base font-medium text-gray-900">
                        {interview.prompt_name || '-'}
                      </TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{interview.round_count || 0}</TableCell>
                      <TableCell className="py-4 max-w-xs truncate text-base text-gray-700">
                        {interview.summary || '-'}
                      </TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">
                        {new Date(interview.created).toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell className="py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2 justify-end">
                          <button
                            type="button"
                            onClick={() => handleView(interview)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            aria-label="詳細"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(interview)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                            aria-label="削除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* ページネーション */}
              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-base text-gray-700">
                    全 {data.pagination.total} 件中 {((page - 1) * 20 + 1)} - {Math.min(page * 20, data.pagination.total)} 件を表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 詳細表示ダイアログ */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>面接詳細</DialogTitle>
              <DialogDescription>面接の詳細情報とラウンドデータ</DialogDescription>
            </DialogHeader>
            {selectedInterview && (
              <div className="space-y-6">
                {/* 基本情報 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-gray-500">ユーザー</div>
                    <div className="text-base mt-1">
                      {selectedInterview.interview?.user_name || '-'}
                      {selectedInterview.interview?.user_email && (
                        <span className="text-gray-500 ml-2">({selectedInterview.interview.user_email})</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">プロンプト</div>
                    <div className="text-base mt-1">{selectedInterview.interview?.prompt_name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">作成日時</div>
                    <div className="text-base mt-1">
                      {selectedInterview.interview?.created
                        ? new Date(selectedInterview.interview.created).toLocaleString('ja-JP')
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-500">更新日時</div>
                    <div className="text-base mt-1">
                      {selectedInterview.interview?.updated
                        ? new Date(selectedInterview.interview.updated).toLocaleString('ja-JP')
                        : '-'}
                    </div>
                  </div>
                </div>
                {selectedInterview.interview?.summary && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-2">概要</div>
                    <div className="text-base bg-gray-50 p-3 rounded whitespace-pre-wrap">
                      {selectedInterview.interview.summary}
                    </div>
                  </div>
                )}
                {selectedInterview.interview?.metadata && (
                  <div>
                    <div className="text-sm font-medium text-gray-500 mb-2">メタデータ</div>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-40">
                      {JSON.stringify(selectedInterview.interview.metadata, null, 2)}
                    </pre>
                  </div>
                )}
                {selectedInterview.rounds && selectedInterview.rounds.length > 0 && (
                  <div>
                    <strong className="text-lg">ラウンド ({selectedInterview.rounds.length}件)</strong>
                    <div className="mt-4 space-y-4">
                      {selectedInterview.rounds.map((round: any, index: number) => {
                        // speaker_typeを取得（round_dataからも取得を試みる）
                        const speakerType = round.speaker_type || (round.round_data && typeof round.round_data === 'object' ? round.round_data.speakerType : null) || 'patient';
                        return (
                          <div key={round.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-sm font-semibold text-medical-blue">
                                ラウンド {index + 1}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(round.created).toLocaleString('ja-JP')}
                              </div>
                            </div>
                            <div className="space-y-3">
                              {/* 話者タイプの表示 */}
                              <div className="text-xs font-medium px-2 py-1 rounded bg-gray-100 inline-block">
                                {speakerType === 'dentist' ? '🦷 歯科医アバター' : '👤 患者アバター'}
                              </div>
                              <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                                <div className="text-xs font-medium text-blue-700 mb-1">ユーザーの質問</div>
                                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                  {round.user_text || '（質問なし）'}
                                </div>
                                {round.user_audio_url && (
                                  <div className="mt-2">
                                    <a
                                      href={round.user_audio_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      🔊 音声ファイルを再生
                                    </a>
                                  </div>
                                )}
                              </div>
                              <div className={`p-3 rounded border-l-4 ${
                                speakerType === 'dentist' 
                                  ? 'bg-purple-50 border-purple-500' 
                                  : 'bg-green-50 border-green-500'
                              }`}>
                                <div className={`text-xs font-medium mb-1 ${
                                  speakerType === 'dentist' 
                                    ? 'text-purple-700' 
                                    : 'text-green-700'
                                }`}>
                                  {speakerType === 'dentist' ? '🦷 歯科医アバターの回答' : '👤 患者アバターの回答'}
                                </div>
                                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                  {round.avatar_text || '（回答なし）'}
                                </div>
                                {round.avatar_audio_url && (
                                  <div className="mt-2">
                                    <a
                                      href={round.avatar_audio_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`text-xs hover:underline ${
                                        speakerType === 'dentist' 
                                          ? 'text-purple-600' 
                                          : 'text-green-600'
                                      }`}
                                    >
                                      🔊 音声ファイルを再生
                                    </a>
                                  </div>
                                )}
                              </div>
                              {round.round_data && (
                                <div className="text-xs text-gray-500 mt-2">
                                  <details>
                                    <summary className="cursor-pointer hover:text-gray-700">
                                      詳細情報を表示
                                    </summary>
                                    <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto">
                                      {JSON.stringify(round.round_data, null, 2)}
                                    </pre>
                                  </details>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {(!selectedInterview.rounds || selectedInterview.rounds.length === 0) && (
                  <div className="text-gray-500 text-sm">ラウンドデータがありません</div>
                )}
                {selectedInterview.scores && selectedInterview.scores.length > 0 && (
                  <div>
                    <strong>スコア ({selectedInterview.scores.length}件):</strong>
                    <div className="mt-2 space-y-2">
                      {selectedInterview.scores.map((score: any) => (
                        <div key={score.id} className="bg-gray-50 p-3 rounded">
                          <div className="text-sm">
                            スコア: {score.score_value} ({score.score_type || '-'})
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* 削除確認ダイアログ */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>面接を削除しますか？</AlertDialogTitle>
              <AlertDialogDescription>
                この操作は取り消せません。面接ID「{selectedInterview?.id}」を削除しますか？
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>キャンセル</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : '削除'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}

